import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import {
  Package, ShoppingCart, RefreshCw, Loader2, ArrowRight, Save,
  CheckCircle, Clock, Truck, PackageCheck, AlertTriangle, Boxes, Edit, Plus,
  X, Crosshair, Eye, Volume2, Ruler, Shield, Palette, Trash2
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', icon: Clock, next: 'paid' },
  paid: { label: 'Paye', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle, next: 'preparing' },
  preparing: { label: 'En preparation', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Package, next: 'ready' },
  ready: { label: 'Pret', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', icon: PackageCheck, next: 'shipped' },
  shipped: { label: 'Expedie', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Truck, next: 'delivered' },
  delivered: { label: 'Livre', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle, next: null },
};
const NEXT_LABEL = { pending: 'Marquer paye', paid: 'Commencer preparation', preparing: 'Marquer pret', ready: 'Marquer expedie', shipped: 'Marquer livre' };

const CATEGORIES = ['compact', 'bombe', 'cascade', 'mine', 'renfort', 'chandelle', 'batterie'];
const EFFECTS = ['chrysantheme', 'pivoine', 'palmier', 'saule', 'comete', 'crepitant', 'brocade', 'multicolore', 'dahlia', 'cascade', 'mine', 'crossette', 'falling_leaves', 'fish'];
const PATTERNS = ['sequential', 'fan', 'simultaneous', 'zigzag', 'w_shape', 'single'];
const DETONATIONS = ['forte', 'moyenne', 'faible', 'silencieuse'];
const CAT_F = ['F2', 'F3', 'F4'];

const EMPTY_MODULE = {
  name: '', display_name: '', description: '', technical_description: '',
  category: 'compact', price: '', stock: 0, active: true,
  specs: {
    ballistic: { caliber_mm: 30, shots_count: 1, shots_duration: 5, burst_height: 40, rise_speed: 50, firing_angle: 90, safety_distance: 25, tube_count: 1, firing_pattern: 'sequential' },
    visual: { effect_type: 'chrysantheme', colors: ['#FFD700'], burn_duration: 3, density: 50, bouquet_size: 100, trail: false, strobe: false, glitter: false },
    sound: { detonation: 'moyenne', crackling: false, whistling: false, thunder: false },
    dimensions: { weight_kg: '', width_mm: '', height_mm: '', length_mm: '' },
    regulation: { category_f: 'F4', ce_number: '' },
  },
};

// === SLIDER FIELD ===
const SliderField = ({ label, value, onChange, min, max, step = 1, unit = '' }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <Label className="text-zinc-400 text-xs">{label}</Label>
      <span className="text-cyan-400 font-mono text-xs">{value}{unit}</span>
    </div>
    <Slider min={min} max={max} step={step} value={[value]} onValueChange={([v]) => onChange(v)} className="py-1" />
  </div>
);

// === COLOR PICKER ===
const ColorPicker = ({ colors, onChange }) => {
  const addColor = () => onChange([...colors, '#FF0000']);
  const removeColor = (i) => onChange(colors.filter((_, idx) => idx !== i));
  const updateColor = (i, c) => { const n = [...colors]; n[i] = c; onChange(n); };
  return (
    <div className="space-y-2">
      <Label className="text-zinc-400 text-xs">Couleurs</Label>
      <div className="flex flex-wrap gap-2 items-center">
        {colors.map((c, i) => (
          <div key={i} className="relative group">
            <input type="color" value={c} onChange={e => updateColor(i, e.target.value)}
              className="w-9 h-9 rounded-lg border-2 border-zinc-700 cursor-pointer bg-transparent" />
            {colors.length > 1 && (
              <button onClick={() => removeColor(i)}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </div>
        ))}
        <button onClick={addColor} className="w-9 h-9 rounded-lg border-2 border-dashed border-zinc-700 text-zinc-500 hover:text-cyan-400 hover:border-cyan-500/50 flex items-center justify-center transition-colors">
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// === MODULE EDITOR ===
const ModuleEditor = ({ module, onSave, onClose, isNew }) => {
  const [form, setForm] = useState(module ? JSON.parse(JSON.stringify(module)) : JSON.parse(JSON.stringify(EMPTY_MODULE)));
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('info');

  const specs = form.specs || EMPTY_MODULE.specs;
  const setSpecs = (section, field, val) => {
    setForm(f => ({ ...f, specs: { ...f.specs, [section]: { ...(f.specs?.[section] || {}), [field]: val } } }));
  };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Nom technique requis'); return; }
    if (!form.price && form.price !== 0) { toast.error('Prix requis'); return; }
    setSaving(true);
    // Build payload - only send non-empty fields
    const payload = {};
    if (form.name?.trim()) payload.name = form.name.trim();
    if (form.display_name?.trim()) payload.display_name = form.display_name.trim();
    if (form.description?.trim()) payload.description = form.description.trim();
    if (form.technical_description?.trim()) payload.technical_description = form.technical_description.trim();
    if (form.category) payload.category = form.category;
    if (form.price !== '' && form.price !== null) payload.price = parseFloat(form.price);
    if (form.stock !== undefined) payload.stock = parseInt(form.stock) || 0;
    payload.active = form.active !== false;

    // Build specs - only include non-empty values
    const buildSection = (data, defaults) => {
      const out = {};
      for (const [k, v] of Object.entries(data || {})) {
        if (v !== '' && v !== null && v !== undefined) {
          if (typeof defaults[k] === 'number') out[k] = typeof v === 'string' ? parseFloat(v) : v;
          else out[k] = v;
        }
      }
      return Object.keys(out).length > 0 ? out : undefined;
    };
    const s = {};
    const b = buildSection(specs.ballistic, EMPTY_MODULE.specs.ballistic); if (b) s.ballistic = b;
    const v = buildSection(specs.visual, EMPTY_MODULE.specs.visual); if (v) s.visual = v;
    // Keep colors array
    if (specs.visual?.colors?.length) { if (!s.visual) s.visual = {}; s.visual.colors = specs.visual.colors; }
    const snd = buildSection(specs.sound, EMPTY_MODULE.specs.sound); if (snd) s.sound = snd;
    const dim = buildSection(specs.dimensions, EMPTY_MODULE.specs.dimensions); if (dim) s.dimensions = dim;
    const reg = buildSection(specs.regulation, EMPTY_MODULE.specs.regulation); if (reg) s.regulation = reg;
    if (Object.keys(s).length > 0) payload.specs = s;

    try {
      await onSave(payload);
      toast.success(isNew ? 'Module cree' : 'Module sauvegarde');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur sauvegarde'));
    }
    setSaving(false);
  };

  const sections = [
    { id: 'info', label: 'Produit', icon: Package },
    { id: 'ballistic', label: 'Balistique', icon: Crosshair },
    { id: 'visual', label: 'Visuel', icon: Eye },
    { id: 'sound', label: 'Sonore', icon: Volume2 },
    { id: 'dimensions', label: 'Dimensions', icon: Ruler },
    { id: 'regulation', label: 'Reglementation', icon: Shield },
  ];

  return (
    <div className="space-y-4">
      {/* Section tabs */}
      <div className="flex gap-1 bg-zinc-950 rounded-lg p-1 overflow-x-auto">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${activeSection === s.id ? 'bg-fuchsia-500/20 text-fuchsia-400' : 'text-zinc-500 hover:text-zinc-300'}`}>
              <Icon className="w-3.5 h-3.5" /> {s.label}
            </button>
          );
        })}
      </div>

      {/* INFO SECTION */}
      {activeSection === 'info' && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-zinc-400 text-xs">Nom technique *</Label>
            <Input value={form.name || ''} onChange={e => setForm({...form, name: e.target.value})} placeholder="nom_technique" className="bg-zinc-950 border-zinc-700 text-white mt-1 font-mono" /></div>
          <div><Label className="text-zinc-400 text-xs">Nom marketing</Label>
            <Input value={form.display_name || ''} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="Bouquet Royal" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div className="col-span-2"><Label className="text-zinc-400 text-xs">Description client</Label>
            <Input value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} placeholder="Un spectacle inoubliable..." className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div className="col-span-2"><Label className="text-zinc-400 text-xs">Description technique</Label>
            <Input value={form.technical_description || ''} onChange={e => setForm({...form, technical_description: e.target.value})} placeholder="Compact 25 tirs, calibre 20mm" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div><Label className="text-zinc-400 text-xs">Categorie</Label>
            <Select value={form.category || 'compact'} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-white capitalize">{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-zinc-400 text-xs">Prix (EUR) *</Label>
            <Input type="number" value={form.price ?? ''} onChange={e => setForm({...form, price: e.target.value})} placeholder="85.00" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div><Label className="text-zinc-400 text-xs">Stock</Label>
            <Input type="number" value={form.stock ?? 0} onChange={e => setForm({...form, stock: e.target.value})} className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div className="flex items-center gap-3 pt-5">
            <Switch checked={form.active !== false} onCheckedChange={v => setForm({...form, active: v})} />
            <Label className="text-zinc-300 text-sm">{form.active !== false ? 'Actif' : 'Inactif'}</Label>
          </div>
        </div>
      )}

      {/* BALLISTIC SECTION */}
      {activeSection === 'ballistic' && (
        <div className="space-y-4">
          <SliderField label="Calibre" value={specs.ballistic?.caliber_mm || 30} onChange={v => setSpecs('ballistic','caliber_mm',v)} min={0} max={150} step={5} unit="mm" />
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-zinc-400 text-xs">Nombre de tirs</Label>
              <Input type="number" value={specs.ballistic?.shots_count || 1} onChange={e => setSpecs('ballistic','shots_count',parseInt(e.target.value)||1)} min={1} max={200} className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
            <div><Label className="text-zinc-400 text-xs">Nombre de tubes</Label>
              <Input type="number" value={specs.ballistic?.tube_count || 1} onChange={e => setSpecs('ballistic','tube_count',parseInt(e.target.value)||1)} min={1} max={20} className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          </div>
          <SliderField label="Duree des tirs" value={specs.ballistic?.shots_duration || 5} onChange={v => setSpecs('ballistic','shots_duration',v)} min={1} max={60} step={0.5} unit="s" />
          <SliderField label="Hauteur d'eclatement" value={specs.ballistic?.burst_height || 40} onChange={v => setSpecs('ballistic','burst_height',v)} min={5} max={100} unit="m" />
          <SliderField label="Vitesse de montee" value={specs.ballistic?.rise_speed || 50} onChange={v => setSpecs('ballistic','rise_speed',v)} min={10} max={100} unit="m/s" />
          <SliderField label="Angle de tir" value={specs.ballistic?.firing_angle || 90} onChange={v => setSpecs('ballistic','firing_angle',v)} min={60} max={90} unit="deg" />
          <SliderField label="Distance de securite" value={specs.ballistic?.safety_distance || 25} onChange={v => setSpecs('ballistic','safety_distance',v)} min={5} max={100} unit="m" />
          <div><Label className="text-zinc-400 text-xs">Mode de tir</Label>
            <Select value={specs.ballistic?.firing_pattern || 'sequential'} onValueChange={v => setSpecs('ballistic','firing_pattern',v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{PATTERNS.map(p => <SelectItem key={p} value={p} className="text-white">{p}</SelectItem>)}</SelectContent>
            </Select></div>
        </div>
      )}

      {/* VISUAL SECTION */}
      {activeSection === 'visual' && (
        <div className="space-y-4">
          <div><Label className="text-zinc-400 text-xs">Type d'effet</Label>
            <Select value={specs.visual?.effect_type || 'chrysantheme'} onValueChange={v => setSpecs('visual','effect_type',v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{EFFECTS.map(e => <SelectItem key={e} value={e} className="text-white capitalize">{e}</SelectItem>)}</SelectContent>
            </Select></div>
          <ColorPicker colors={specs.visual?.colors || ['#FFD700']} onChange={c => setSpecs('visual','colors',c)} />
          <SliderField label="Duree de combustion" value={specs.visual?.burn_duration || 3} onChange={v => setSpecs('visual','burn_duration',v)} min={0.5} max={10} step={0.5} unit="s" />
          <SliderField label="Densite (particules)" value={specs.visual?.density || 50} onChange={v => setSpecs('visual','density',v)} min={1} max={100} />
          <SliderField label="Taille du bouquet" value={specs.visual?.bouquet_size || 100} onChange={v => setSpecs('visual','bouquet_size',v)} min={20} max={250} />
          <div className="grid grid-cols-3 gap-4 pt-1">
            <div className="flex items-center gap-2"><Switch checked={!!specs.visual?.trail} onCheckedChange={v => setSpecs('visual','trail',v)} /><Label className="text-zinc-300 text-xs">Trainee</Label></div>
            <div className="flex items-center gap-2"><Switch checked={!!specs.visual?.strobe} onCheckedChange={v => setSpecs('visual','strobe',v)} /><Label className="text-zinc-300 text-xs">Strobe</Label></div>
            <div className="flex items-center gap-2"><Switch checked={!!specs.visual?.glitter} onCheckedChange={v => setSpecs('visual','glitter',v)} /><Label className="text-zinc-300 text-xs">Glitter</Label></div>
          </div>
        </div>
      )}

      {/* SOUND SECTION */}
      {activeSection === 'sound' && (
        <div className="space-y-4">
          <div><Label className="text-zinc-400 text-xs">Detonation</Label>
            <Select value={specs.sound?.detonation || 'moyenne'} onValueChange={v => setSpecs('sound','detonation',v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{DETONATIONS.map(d => <SelectItem key={d} value={d} className="text-white capitalize">{d}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-3 gap-4 pt-2">
            <div className="flex items-center gap-2"><Switch checked={!!specs.sound?.crackling} onCheckedChange={v => setSpecs('sound','crackling',v)} /><Label className="text-zinc-300 text-xs">Crepitement</Label></div>
            <div className="flex items-center gap-2"><Switch checked={!!specs.sound?.whistling} onCheckedChange={v => setSpecs('sound','whistling',v)} /><Label className="text-zinc-300 text-xs">Sifflement</Label></div>
            <div className="flex items-center gap-2"><Switch checked={!!specs.sound?.thunder} onCheckedChange={v => setSpecs('sound','thunder',v)} /><Label className="text-zinc-300 text-xs">Tonnerre</Label></div>
          </div>
        </div>
      )}

      {/* DIMENSIONS SECTION */}
      {activeSection === 'dimensions' && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-zinc-400 text-xs">Poids (kg)</Label>
            <Input type="number" step="0.1" value={specs.dimensions?.weight_kg ?? ''} onChange={e => setSpecs('dimensions','weight_kg',e.target.value)} placeholder="2.5" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div><Label className="text-zinc-400 text-xs">Largeur (mm)</Label>
            <Input type="number" value={specs.dimensions?.width_mm ?? ''} onChange={e => setSpecs('dimensions','width_mm',e.target.value)} placeholder="95" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div><Label className="text-zinc-400 text-xs">Hauteur (mm)</Label>
            <Input type="number" value={specs.dimensions?.height_mm ?? ''} onChange={e => setSpecs('dimensions','height_mm',e.target.value)} placeholder="350" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
          <div><Label className="text-zinc-400 text-xs">Longueur (mm)</Label>
            <Input type="number" value={specs.dimensions?.length_mm ?? ''} onChange={e => setSpecs('dimensions','length_mm',e.target.value)} placeholder="95" className="bg-zinc-950 border-zinc-700 text-white mt-1" /></div>
        </div>
      )}

      {/* REGULATION SECTION */}
      {activeSection === 'regulation' && (
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-zinc-400 text-xs">Categorie F</Label>
            <Select value={specs.regulation?.category_f || 'F4'} onValueChange={v => setSpecs('regulation','category_f',v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">{CAT_F.map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-zinc-400 text-xs">Numero CE</Label>
            <Input value={specs.regulation?.ce_number || ''} onChange={e => setSpecs('regulation','ce_number',e.target.value)} placeholder="CE-0000-XXXX" className="bg-zinc-950 border-zinc-700 text-white mt-1 font-mono" /></div>
        </div>
      )}

      {/* Save button */}
      <div className="flex gap-2 pt-3 border-t border-zinc-800">
        <Button variant="outline" className="flex-1 border-zinc-700 text-zinc-300" onClick={onClose}>Annuler</Button>
        <Button className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 text-white" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {isNew ? 'Creer le module' : 'Sauvegarder'}
        </Button>
      </div>
    </div>
  );
};

// === MAIN PAGE ===
const PyroDistrictPage = () => {
  const [modules, setModules] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(null);
  const [editModule, setEditModule] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const configRes = await api.get('/pyrodistrict/config');
      if (!configRes.data.configured) { setConfigured(false); setLoading(false); return; }
      setConfigured(true);
      const [modRes, ordRes] = await Promise.all([
        api.get('/pyrodistrict/modules').catch(() => ({ data: [] })),
        api.get('/pyrodistrict/orders').catch(() => ({ data: [] })),
      ]);
      setModules(Array.isArray(modRes.data) ? modRes.data : modRes.data?.modules || modRes.data?.items || []);
      setOrders(Array.isArray(ordRes.data) ? ordRes.data : ordRes.data?.orders || ordRes.data?.items || []);
    } catch { toast.error('Erreur connexion PyroDistrict'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveModule = async (payload) => {
    if (isNew) {
      await api.post('/pyrodistrict/modules', payload);
    } else {
      const id = editModule?.id || editModule?._id;
      await api.put(`/pyrodistrict/modules/${id}`, payload);
    }
    setEditModule(null);
    fetchData();
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await api.put(`/pyrodistrict/orders/${orderId}/status`, { status: newStatus });
      toast.success(`Statut mis a jour : ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchData();
    } catch (err) { toast.error(getApiErrorMessage(err, 'Erreur')); }
    setUpdatingStatus(null);
  };

  if (configured === false) {
    return (
      <div className="space-y-6" data-testid="pyrodistrict-page">
        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2"><Boxes className="w-6 h-6 text-fuchsia-400" /> PyroDistrict</h1>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg text-white font-semibold mb-2">PyroDistrict non configure</h2>
          <p className="text-zinc-400 text-sm mb-4">Ajoutez PYRODISTRICT_API_URL et PYRODISTRICT_API_KEY dans le .env</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pyrodistrict-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2"><Boxes className="w-6 h-6 text-fuchsia-400" /> PyroDistrict</h1>
          <p className="text-zinc-400 text-sm mt-1">Modules et commandes PyroDistrict</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" className="border-zinc-700 text-zinc-300 gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white gap-2" onClick={() => { setEditModule({}); setIsNew(true); }}>
            <Plus className="w-4 h-4" /> Nouveau Module
          </Button>
        </div>
      </div>

      <Tabs defaultValue="modules" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="modules" className="data-[state=active]:bg-fuchsia-500/20 data-[state=active]:text-fuchsia-400">
            <Package className="w-4 h-4 mr-2" /> Modules ({modules.length})
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-fuchsia-500/20 data-[state=active]:text-fuchsia-400">
            <ShoppingCart className="w-4 h-4 mr-2" /> Commandes ({orders.length})
          </TabsTrigger>
        </TabsList>

        {/* MODULES */}
        <TabsContent value="modules" className="mt-4">
          <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-4">
            {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-fuchsia-400 animate-spin" /></div>
            : modules.length === 0 ? <p className="text-zinc-500 text-center py-8">Aucun module</p>
            : <div className="space-y-2">
              {modules.map(m => {
                const id = m.id || m._id || m.module_id;
                return (
                  <div key={id} className="flex items-center justify-between p-3 bg-zinc-950 rounded-lg border border-zinc-800 hover:border-fuchsia-500/30 transition-colors cursor-pointer"
                    onClick={() => { setEditModule(m); setIsNew(false); }} data-testid={`module-${id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-fuchsia-500/10 flex items-center justify-center">
                        <Package className="w-5 h-5 text-fuchsia-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{m.display_name || m.name}</p>
                        <p className="text-zinc-500 text-xs">{m.name} — {m.category || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-amber-400 font-mono text-sm font-bold">{m.price ? `${m.price} EUR` : '-'}</p>
                        <p className={`font-mono text-xs ${(m.stock || 0) === 0 ? 'text-red-400' : 'text-green-400'}`}>Stock: {m.stock ?? 0}</p>
                      </div>
                      <Badge className={m.active !== false ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>
                        {m.active !== false ? 'Actif' : 'Inactif'}
                      </Badge>
                      <Edit className="w-4 h-4 text-zinc-500" />
                    </div>
                  </div>
                );
              })}
            </div>}
          </CardContent></Card>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-4">
          <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="pt-4">
            {loading ? <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 text-fuchsia-400 animate-spin" /></div>
            : orders.length === 0 ? <p className="text-zinc-500 text-center py-8">Aucune commande</p>
            : <div className="space-y-3">
              {orders.map(order => {
                const id = order.id || order._id || order.order_id;
                const status = order.status || 'pending';
                const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.icon;
                const items = order.items || order.modules || [];
                return (
                  <div key={id} className="bg-zinc-950 rounded-lg border border-zinc-800 p-4" data-testid={`order-${id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <StatusIcon className="w-5 h-5 text-zinc-400" />
                        <div>
                          <span className="text-white font-medium text-sm">Commande {order.order_number || id}</span>
                          {order.customer_name && <span className="text-zinc-500 text-xs ml-2">— {order.customer_name}</span>}
                        </div>
                        <Badge variant="outline" className={`text-xs ${cfg.color}`}>{cfg.label}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        {order.created_at && <span className="text-zinc-600 text-xs">{new Date(order.created_at).toLocaleDateString('fr-FR')}</span>}
                        {cfg.next && (
                          <Button size="sm" className="h-7 text-xs bg-fuchsia-600 hover:bg-fuchsia-500 text-white gap-1"
                            disabled={updatingStatus === id} onClick={() => handleUpdateStatus(id, cfg.next)}>
                            {updatingStatus === id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                            {NEXT_LABEL[status]}
                          </Button>
                        )}
                      </div>
                    </div>
                    {items.length > 0 && (
                      <div className="bg-zinc-900 rounded border border-zinc-800 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead><tr className="border-b border-zinc-800"><th className="text-left py-2 px-3 text-zinc-500">Module</th><th className="text-left py-2 px-3 text-zinc-500">Reference</th><th className="text-right py-2 px-3 text-zinc-500">Quantite</th></tr></thead>
                          <tbody>{items.map((item, i) => (
                            <tr key={i} className="border-b border-zinc-800/50">
                              <td className="py-2 px-3 text-white">{item.name || item.module_name || item.title || '-'}</td>
                              <td className="py-2 px-3 font-mono text-cyan-400">{item.reference || item.ref || '-'}</td>
                              <td className="py-2 px-3 text-right text-amber-400 font-mono">{item.quantity || 1}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    {order.notes && <p className="text-zinc-500 text-xs mt-2 italic">{order.notes}</p>}
                  </div>
                );
              })}
            </div>}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      {/* Module Editor Dialog */}
      <Dialog open={!!editModule} onOpenChange={() => setEditModule(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {isNew ? <Plus className="w-5 h-5 text-fuchsia-400" /> : <Edit className="w-5 h-5 text-fuchsia-400" />}
              {isNew ? 'Nouveau Module' : `Editer : ${editModule?.display_name || editModule?.name || ''}`}
            </DialogTitle>
          </DialogHeader>
          <ModuleEditor module={editModule} onSave={handleSaveModule} onClose={() => setEditModule(null)} isNew={isNew} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PyroDistrictPage;
